/**
 * 文生图服务 - 火山 Seedream / 钱多多 OpenAI 兼容 / 阿里 DashScope 代理
 */

import { alibabaAI, modelAPI } from './api';

export interface ImageModelConfig {
  id: string;
  name: string;
  type: string;
  provider: string;
  api_key: string;
  base_url?: string;
  model_name: string;
  is_active: boolean;
}

const DEFAULT_BASE_URL = 'https://ark.cn-beijing.volces.com/api/v3';

/** 比例 -> Seedream 尺寸（宽x高），API 要求总像素至少 3,686,400 */
const RATIO_TO_SIZE: Record<string, string> = {
  '1:1': '1920x1920',
  '2:3': '1600x2400',
  '3:2': '2400x1600',
  '3:4': '1680x2240',
  '4:3': '2240x1680',
  '4:5': '1728x2160',
  '5:4': '2160x1728',
  '9:16': '1440x2560',
  '16:9': '2560x1440',
  '21:9': '2940x1260',
};

/**
 * 从后台获取当前启用的图片生成模型配置
 */
export async function getImageModelConfig(): Promise<ImageModelConfig | null> {
  try {
    const models = await modelAPI.getAllModels();
    const imageModels = models
      .filter(model => model.is_active && model.type === 'image')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return imageModels[0] as ImageModelConfig | null;
  } catch (error) {
    console.error('获取图片模型配置失败:', error);
    return null;
  }
}

/**
 * 调用图片生成 API，返回生成图片 URL
 */
export async function generateImage(
  config: ImageModelConfig,
  prompt: string,
  ratio: string = '1:1'
): Promise<string> {
  if (config.provider === 'alibaba') {
    const data = await alibabaAI.image({
      prompt: prompt.trim(),
      ratio,
      model_id: config.id,
    });
    if (!data?.url) throw new Error('接口未返回图片');
    return data.url;
  }

  const baseUrl = (config.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
  const url = `${baseUrl}/images/generations`;
  const size = RATIO_TO_SIZE[ratio] || RATIO_TO_SIZE['1:1'];

  const body = {
    model: config.model_name,
    prompt: prompt.trim(),
    n: 1,
    size,
    response_format: 'url' as const,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.api_key}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: { message?: string }; message?: string };
    const msg = err?.error?.message || err?.message || `请求失败 ${response.status}`;
    throw new Error(msg);
  }

  const data = (await response.json()) as {
    data?: Array<{ url?: string; b64_json?: string }>;
    images?: Array<{ url?: string }>;
  };

  const list = data.data ?? data.images;
  if (Array.isArray(list) && list.length > 0) {
    const first = list[0];
    if (typeof first?.url === 'string') return first.url;
    if (typeof (first as { b64_json?: string }).b64_json === 'string') {
      return `data:image/png;base64,${(first as { b64_json: string }).b64_json}`;
    }
  }
  throw new Error('接口未返回图片');
}
